import { AnimatePresence } from "framer-motion"
import { useEffect, useRef, useState } from "react"
import type { Media } from "@/shared/lib/types"
import { MediaThumbnailSlider } from "./media-thumbnail-slider"
import { PostMediaFullscreen } from "./post-media-fullscreen"
import { PostMediaItem } from "./post-media-item"
import { PostMediaPrimaryPhoto } from "./post-media-primary-photo"
import { partitionPostMedia, STICKY_TOP_OFFSET } from "./post-media-viewer.utils"
import { useMediaBlobUrl } from "./use-media-blob-url"
import { useVideoPlaybackProgress } from "./use-video-playback-progress"

interface PostMediaViewerProps {
	media: Media[]
	className?: string
	compact?: boolean
	postId?: string
}

export function PostMediaViewer({
	media,
	className = "",
	compact = false,
	postId,
}: PostMediaViewerProps) {
	const [fullscreenImage, setFullscreenImage] = useState<string | null>(null)
	const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null)
	const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null)
	const videoRef = useRef<HTMLVideoElement>(null)
	const [reservedHeight, setReservedHeight] = useState<number>()
	const { loadMediaAsBlob } = useMediaBlobUrl()

	const { postMedia, videoMedia, photoMedia, otherMedia } = partitionPostMedia(media)

	const { saveProgress, prepareVideoSwitch, tryRestoreOnMetadata } = useVideoPlaybackProgress({
		postId,
		selectedVideoId,
		videoRef,
	})

	useEffect(() => {
		if (videoMedia.length === 0) return
		if (!selectedVideoId) {
			setSelectedVideoId(videoMedia[0].id)
			return
		}
		if (!videoMedia.some((m) => m.id === selectedVideoId)) {
			setSelectedVideoId(videoMedia[0].id)
		}
	}, [videoMedia, selectedVideoId])

	useEffect(() => {
		if (photoMedia.length === 0) return
		if (!selectedPhotoId) {
			setSelectedPhotoId(photoMedia[0].id)
			return
		}
		if (!photoMedia.some((m) => m.id === selectedPhotoId)) {
			setSelectedPhotoId(photoMedia[0].id)
		}
	}, [photoMedia, selectedPhotoId])

	if (!media?.length || !postMedia.length) {
		return null
	}

	const selectedVideo = videoMedia.find((m) => m.id === selectedVideoId) ?? videoMedia[0]
	const selectedPhoto = photoMedia.find((m) => m.id === selectedPhotoId) ?? photoMedia[0]

	return (
		<>
			<div className={compact ? className : "contents"}>
				{videoMedia.length > 0 && selectedVideo ? (
					<>
						<div
							className={compact ? "w-full" : "relative sticky z-20 w-full bg-background shadow-md"}
							style={compact ? undefined : { top: STICKY_TOP_OFFSET }}
						>
							{!compact ? (
								<div
									aria-hidden="true"
									className="pointer-events-none absolute right-0 left-0 bg-background"
									style={{
										height: STICKY_TOP_OFFSET,
										top: `calc(-1 * (${STICKY_TOP_OFFSET}))`,
									}}
								/>
							) : null}
							<PostMediaItem
								item={selectedVideo}
								index={0}
								compact={false}
								isPrimaryVideo
								videoRef={videoRef}
								onVideoPause={saveProgress}
								onVideoSeeking={saveProgress}
								minHeight={reservedHeight}
								onVideoLoadedMetadata={(e) => {
									setReservedHeight(undefined)
									tryRestoreOnMetadata(selectedVideo.id, e.currentTarget)
								}}
							/>
						</div>

						<MediaThumbnailSlider
							selectedId={selectedVideo.id}
							onSelect={(id) => {
								setReservedHeight(videoRef.current?.offsetHeight)
								saveProgress()
								prepareVideoSwitch(id)
								setSelectedVideoId(id)
							}}
							items={videoMedia.map((item) => ({
								id: item.id,
								thumbnail: (
									<div className="relative h-24 overflow-hidden rounded-[0.625rem] bg-muted">
										{item.thumbnailUrl ? (
											<img
												src={item.thumbnailUrl}
												alt="Превью видео"
												className="h-full w-full object-cover"
												loading="lazy"
												onContextMenu={(e) => e.preventDefault()}
												onDragStart={(e) => e.preventDefault()}
											/>
										) : (
											<div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
												Видео
											</div>
										)}
									</div>
								),
							}))}
						/>
					</>
				) : null}

				{photoMedia.length > 0 && selectedPhoto ? (
					<div className="mb-4 flex flex-col gap-3">
						<div className="w-full">
							<PostMediaPrimaryPhoto
								item={selectedPhoto}
								compact={compact}
								loadMediaAsBlob={loadMediaAsBlob}
								onOpenFullscreen={setFullscreenImage}
							/>
						</div>

						<MediaThumbnailSlider
							className=""
							selectedId={selectedPhoto.id}
							onSelect={setSelectedPhotoId}
							items={photoMedia.map((item) => ({
								id: item.id,
								thumbnail: (
									<div className="relative h-24 overflow-hidden rounded-[0.625rem] bg-muted">
										<img
											src={item.url}
											alt="Превью фото"
											className="h-full w-full object-cover"
											loading="lazy"
											onContextMenu={(e) => e.preventDefault()}
											onDragStart={(e) => e.preventDefault()}
										/>
									</div>
								),
							}))}
						/>
					</div>
				) : null}

				{otherMedia.length > 0
					? (() => {
							const useGrid = otherMedia.length > 1
							const compactMode = compact || useGrid
							return (
								<div className={useGrid ? "grid grid-cols-2 gap-3" : "flex flex-col gap-3"}>
									{otherMedia.map((item, index) => (
										<div key={item.id} className={useGrid && index === 0 ? "col-span-2" : ""}>
											<PostMediaItem item={item} index={index + 1} compact={compactMode} />
										</div>
									))}
								</div>
							)
						})()
					: null}
			</div>

			<AnimatePresence>
				{fullscreenImage ? (
					<PostMediaFullscreen
						imageUrl={fullscreenImage}
						onClose={() => setFullscreenImage(null)}
					/>
				) : null}
			</AnimatePresence>
		</>
	)
}
